import { Component, Input, OnDestroy, OnInit, ViewChild, } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { StateNotifyService } from './common/state/service/state-notify.service';
import { Vessel } from './feature/vessel/dto/vessel';
import { Customer } from './feature/customer/dto/customer';
import { Subject, takeUntil } from 'rxjs';
import { Trail } from './feature/trail/dto/trail';
import { Test } from './feature/test/dto/test';
import { TestStep } from './feature/test-step/dto/test-step';
import { Recommendation } from './feature/recommendation/dto/recommendation';
import { UserEditRightService } from './common/user-role/service/user-edit-right.service';
import { Title } from '@angular/platform-browser';
import { StepRecom } from './feature/step-recom/interface/step-recom';

@Component({
  selector: 'dpi-root',
  imports: [RouterOutlet, MatSidenavModule, MatListModule, MatToolbarModule, MatButtonToggleModule, MatFormFieldModule, MatInputModule, MatButtonModule,
    MatMenuModule, MatTooltipModule, MatIconModule, MatCardModule, NgFor, NgIf],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('sidenav') sideNav: any;
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  title = 'ADP';

  isLoggedIn: boolean;
  isWithEditRight: boolean;
  isReadonly: boolean;

  currentModelInSelection = 0; // 0: none; 1: customer; 2: vessel; 3: trail; 4: test; 5: test step; 6: recommendation; 7: step recom
  listAllCustomers: Customer[] = [];
  selectedCustomer = { id: 0, name: 'Customer...' };
  customerName = '';
  listAllVessel: Vessel[] = [];
  selectedVessel = { id: 0, name: 'Vessel...', customer_id: 0 };
  vesselName = '';
  listAllTrail: Trail[] = [];
  selectedTrail = { id: 0, vessel_id: 0, name: 'Trail...', };
  trailName = '';
  listAllTest: Test[] = [];
  selectedTest = { id: 0, trail_id: 0, vessel_id: 0, sn: '0', name: 'Test...', }
  testName = '';
  listAllTestStep: TestStep[] = [];
  selectedTestStep = { id: 0, test_id: 0, sn: '0', }
  testStepSN = '';
  listAllRecommendation: Recommendation[] = [];
  selectedRecommendation = { id: 0, test_id: 0, sn: '0', }
  recommendationSN = '';
  listAllStepRecom: StepRecom[] = [];
  selectedStepRecom = { id: 0, test_step_id: 0, sn: '0',}
  stepRecomSN = '';

  constructor(private notifyService: StateNotifyService
    , private router: Router
    , private userRightService: UserEditRightService
    , private titleService: Title) {
    this.isLoggedIn = this.notifyService.getLoginState();
    this.isWithEditRight = this.notifyService.getUserEditRight();
    this.isReadonly = this.notifyService.getReadonlyMode();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.titleService.setTitle(this.title);

    //--- Login ---
    // Subscribe the currentSelection property of customer selection service to get real time value
    this.notifyService.loginState$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        state => this.isLoggedIn = state
      );

    //--- Edit mode ---
    this.notifyService.hasEditRight$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        state => this.isWithEditRight = state
      );

    //--- Display mode ---
    this.notifyService.currentDisplayMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        state => this.currentModelInSelection = state
      );

    //--- Customer ---
    this.notifyService.customerSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        selection => {
          this.selectedCustomer = selection;
          let name = this.selectedCustomer.name;
          this.customerName = (name.length > 12) ? name.slice(0, 12) + '...' : name;
          this.currentModelInSelection = 1;
        }
      );

    this.notifyService.allCustomersList$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        list => {
          this.listAllCustomers = list;
        }
      )

    //--- Vessel ---
    this.notifyService.vesselSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        selection => {
          this.selectedVessel = selection;
          let name = this.selectedVessel.name;
          this.vesselName = (name.length > 12) ? name.slice(0, 12) + '...' : name;
          this.currentModelInSelection = 2;
        }
      );

    this.notifyService.allVesselsList$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        list => {
          this.listAllVessel = list;
        }
      )

    //--- Trail ---
    this.notifyService.trailSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        selection => {
          this.selectedTrail = selection;
          let name = this.selectedTrail.name;
          this.trailName = (name.length > 12) ? name.slice(0, 12) + '...' : name;
          this.currentModelInSelection = 3;
        }
      );

    this.notifyService.allTrailsList$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        list => {
          this.listAllTrail = list;
        }
      )

    //--- Test ---
    this.notifyService.testSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        selection => {
          this.selectedTest = selection;
          let name = this.selectedTest.name;
          this.testName = (name.length > 12) ? name.slice(0, 12) + '...' : name;
          this.currentModelInSelection = 4;
        }
      );

    this.notifyService.allTestsList$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        list => {
          this.listAllTest = list;
        }
      )

    //--- Test step---
    this.notifyService.testStepSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        selection => {
          this.selectedTestStep = selection;
          let name = this.selectedTestStep.sn;
          this.testStepSN = (name.length > 12) ? name.slice(0, 12) + '...' : name;
          this.currentModelInSelection = 5;
        }
      );

    this.notifyService.allTestStepList$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        list => {
          this.listAllTestStep = list;
        }
      )

    //--- Recommendation---
    this.notifyService.recommendationSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        selection => {
          this.selectedRecommendation = selection;
          let name = this.selectedRecommendation.sn;
          this.recommendationSN = (name.length > 12) ? name.slice(0, 12) + '...' : name;
          this.currentModelInSelection = 6;
        }
      );

    this.notifyService.allRecommendationList$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        list => {
          this.listAllRecommendation = list;
        }
      )

    //--- Step Recommendation---
    this.notifyService.stepRecomSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        selection => {
          this.selectedStepRecom = selection; 
          let name = this.selectedStepRecom.sn;
          this.stepRecomSN = (name.length > 12) ? name.slice(0, 12) + '...' : name;
          this.currentModelInSelection = 7;
        }
      );

    this.notifyService.allStepRecomList$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        list => {
          this.listAllStepRecom = list;
        }
      )

  }

  //------ Main toolbar ------

  toggleSideNave() {
    this.sideNav.toggle();
  }

  onEditModeChange(value: any) {
    if (value === 'edit') {
      this.isReadonly = false;
    } else {
      this.isReadonly = true;
    }
    this.notifyService.updateReadonlyMode(this.isReadonly);
  }

  homeButtonClicked() {
    this.router.navigate(['customers']);
    this.currentModelInSelection = 0;
    this.notifyService.updateUserEditRight(false);
    this.notifyService.updateReadonlyMode(true);
    //this.resetSelectedCustomer();
  }

  customerButtonClicked() {
    this.router.navigate(['customer-details']);
    this.currentModelInSelection = 1;
    //this.resetSelectedVessel();
  }

  vesselButtonClicked() {
    this.router.navigate(['vessel-hub']);
    this.currentModelInSelection = 2;
    //this.resetSelectedTrail();
  }

  trialButtonClicked() {
    this.router.navigate(['trail-hub']);
    this.currentModelInSelection = 3;
    //this.resetSelectedTest();
  }

  testButtonClicked() {
    this.router.navigate(['test-hub']);
    this.currentModelInSelection = 4;
    //this.resetSelectedTestStep();
  }

  testStepButtonClicked() {
    this.router.navigate(['test-step-hub']);
    this.currentModelInSelection = 5;
  }

  recommendationButtonClicked() {
    this.router.navigate(['recommendation-hub']);
    this.currentModelInSelection = 6;
  }

  stepRecomButtonClicked() {
    this.router.navigate(['step-recom-hub']);
    this.currentModelInSelection = 7;
  }

  resetSelectedCustomer() {
    this.selectedCustomer = { id: 0, name: 'Customer...' };
    this.resetSelectedVessel();
  }

  resetSelectedVessel() {
    this.selectedVessel = { id: 0, name: 'Vessel...', customer_id: 0 };
    this.resetSelectedTrail();
  }

  resetSelectedTrail() {
    this.selectedTrail = { id: 0, vessel_id: 0, name: 'Trail...', };
    this.resetSelectedTest();
  }

  resetSelectedTest() {
    this.selectedTest = { id: 0, trail_id: 0, vessel_id: 0, sn: '0', name: 'Trail...', };
    this.resetSelectedTestStep();
  }

  resetSelectedTestStep() {
    this.selectedTestStep = { id: 0, test_id: 0, sn: '0', };
  }

  //------ sideNave ------

  selectCustomer(customer: Customer) {
    this.notifyService.updateCustomerSelection(customer);

    let editRight = this.userRightService.getEditRight(customer.user_role);
    this.notifyService.updateUserEditRight(editRight);

    this.router.navigate(['customer-details']);
  }

  selectVessel(vessel: Vessel) {
    this.notifyService.updateVesselSelection(vessel);
    this.router.navigate(['vessel-hub']);
  }

  selectTrail(trail: Trail) {
    this.notifyService.updateTrailSelection(trail);
    this.router.navigate(['trail-hub']);
  }

  selectTest(test: Test) {
    this.notifyService.updateTestSelection(test);
    this.router.navigate(['test-hub']);
  }

  selectTestStep(step: TestStep) {
    this.notifyService.updateTestStepSelection(step);
    this.router.navigate(['test-step-hub']);
  }

  selectRecommendation(recommendation: Recommendation) {
    this.notifyService.updateRecommendationSelection(recommendation);
    this.router.navigate(['recommendation-hub']);
  }

  selectStepRecom(stepRecom: StepRecom) {
    this.notifyService.updateStepRecomSelection(stepRecom);
    this.router.navigate(['step-recom-hub']);
  }

  download_ADP_manual(): void {
    // public下的文件，访问路径就是 /文件名
    this.downloadFile('/resources/', 'ADP使用手册.pdf');
  }

  download_ADP_apk(): void {
    this.downloadFile('/resources/', 'app-release.apk');
  }

  private downloadFile(url: string, filename: string): void {
    const a = document.createElement('a');
    a.href = url + filename;
    // download 属性：浏览器提示下载，而不是直接打开
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
